import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiService } from '@/services/api';
import { format } from 'date-fns';
import { StatusToggle } from '@/components/StatusToggle';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { ProductCell } from '@/components/ProductCell';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { ProductScopeGate } from '@/components/ProductScopeGate';

export const TermsPage: React.FC = () => {
  const { productId } = useFilters();
  const productsById = useProductsMap();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const { confirm } = useConfirmation();
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    type: 'terms_of_use' as 'terms_of_use' | 'lgpd' | 'resale_prohibition' | 'other',
    title: '',
    content: '',
    effectiveDate: format(new Date(), 'yyyy-MM-dd'),
    expirationDate: '' as string,
    isRequired: true,
    displayOrder: 0,
    language: 'pt-BR'
  });

  const loadDocuments = useCallback(async () => {
    if (!productId?.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.getTermDocuments({
        productId,
        page: page + 1,
        limit: rowsPerPage
      });

      if (response.data.success) {
        setDocuments(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      setLoadError(error);
      setDocuments([]);
      setTotal(0);
      logger.error('Error loading term documents:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, productId]);

  useEffect(() => {
    setPage(0);
    loadDocuments();
  }, [productId, loadDocuments]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Registrar callback de refresh para atualizar apenas o conteúdo da página
  useEffect(() => {
    setRefreshCallback(loadDocuments);
    return () => setRefreshCallback(null);
  }, [loadDocuments, setRefreshCallback]);

  const handleOpenModal = (document?: any) => {
    if (document) {
      // Edição: carregar dados do documento
      setEditingDocument(document);
      setFormData({
        type: document.type,
        title: document.title,
        content: document.content,
        effectiveDate: format(new Date(document.effectiveDate), 'yyyy-MM-dd'),
        expirationDate: document.expirationDate
          ? format(new Date(document.expirationDate), 'yyyy-MM-dd')
          : '',
        isRequired: document.isRequired,
        displayOrder: document.displayOrder || 0,
        language: document.language || 'pt-BR'
      });
    } else {
      // Criação: resetar formulário
      setEditingDocument(null);
      setFormData({
        type: 'terms_of_use',
        title: '',
        content: '',
        effectiveDate: format(new Date(), 'yyyy-MM-dd'),
        expirationDate: '',
        isRequired: true,
        displayOrder: 0,
        language: 'pt-BR'
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingDocument(null);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      showError('Título e conteúdo são obrigatórios');
      return;
    }

    setSaving(true);

    try {
      if (editingDocument) {
        // Edição: cria nova versão e desativa a anterior
        const response = await apiService.updateTermDocument(editingDocument._id, {
          title: formData.title,
          content: formData.content,
          effectiveDate: new Date(formData.effectiveDate),
          expirationDate: formData.expirationDate ? new Date(formData.expirationDate) : null,
          isRequired: formData.isRequired,
          displayOrder: formData.displayOrder,
          language: formData.language
        });

        if (response.data.success) {
          showSuccess('Documento atualizado com sucesso (nova versão criada)');
          handleCloseModal();
          loadDocuments();
        }
      } else {
        // Criação: novo documento
        const response = await apiService.createTermDocument({
          ...formData,
          productId,
          effectiveDate: new Date(formData.effectiveDate),
          expirationDate: formData.expirationDate ? new Date(formData.expirationDate) : null,
          version: '1.0.0'
        });

        if (response.data.success) {
          showSuccess('Documento criado com sucesso');
          handleCloseModal();
          loadDocuments();
        }
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao salvar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = (isActive: boolean, documentId: string) => {
    if (isActive) {
      // Desativar
      confirm({
        title: 'Desativar Documento',
        message: 'Tem certeza que deseja desativar este documento?',
        confirmText: 'Desativar',
        cancelText: 'Cancelar',
        confirmColor: 'error',
        onConfirm: async () => {
          try {
            const response = await apiService.deactivateTermDocument(documentId);
            if (response.data.success) {
              showSuccess('Documento desativado com sucesso');
              loadDocuments();
            }
          } catch (err: any) {
            showError(err.response?.data?.message || 'Erro ao desativar documento');
          }
        }
      });
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'color',
    'background',
    'align',
    'link',
    'image'
  ];

  return (
    <ProductScopeGate
      productId={productId}
      integration="core"
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadDocuments}
    >
      <Box>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Termos de aceite
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Documentos legais por produto: termos de uso, LGPD e políticas adicionais exibidas
                  no fluxo de aceite.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <ProductCell
                    productId={productId}
                    name={productsById.get(productId)?.displayName}
                    avatarUrl={productsById.get(productId)?.avatarUrl}
                  />
                  <Chip size="small" variant="outlined" label="Versão + vigência" />
                  <Chip size="small" variant="outlined" label="Apenas um ativo por tipo" />
                </Stack>
              </Box>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
                Novo Documento
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Versão</TableCell>
                <TableCell>Data de Vigência</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info" sx={{ my: 1, display: 'inline-flex' }}>
                      Não há termos cadastrados para este produto.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map(doc => (
                  <TableRow key={doc._id}>
                    <TableCell>{doc.type}</TableCell>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>{doc.version}</TableCell>
                    <TableCell>{new Date(doc.effectiveDate).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell>
                      <StatusToggle
                        isActive={doc.isActive}
                        onClick={(isActive: boolean) => handleStatusToggle(isActive, doc._id)}
                        disabled={!doc.isActive}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(doc)}
                        color="primary"
                        title="Editar"
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />

        {/* Modal de criação/edição */}
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DialogTitle>
            {editingDocument ? 'Editar Documento de Termo' : 'Novo Documento de Termo'}
            {editingDocument && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Ao salvar, uma nova versão será criada e a versão anterior será desativada
                permanentemente.
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={e => setFormData({ ...formData, type: e.target.value as any })}
                      label="Tipo"
                    >
                      <MenuItem value="terms_of_use">Termo de Uso</MenuItem>
                      <MenuItem value="lgpd">LGPD</MenuItem>
                      <MenuItem value="resale_prohibition">Proibição de Revenda</MenuItem>
                      <MenuItem value="other">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Vigência"
                    type="date"
                    value={formData.effectiveDate}
                    onChange={e => setFormData({ ...formData, effectiveDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Expiração (opcional)"
                    type="date"
                    value={formData.expirationDate}
                    onChange={e => setFormData({ ...formData, expirationDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ordem de Exibição"
                    type="number"
                    value={formData.displayOrder}
                    onChange={e =>
                      setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Idioma</InputLabel>
                    <Select
                      value={formData.language}
                      onChange={e => setFormData({ ...formData, language: e.target.value })}
                      label="Idioma"
                    >
                      <MenuItem value="pt-BR">Português (Brasil)</MenuItem>
                      <MenuItem value="en-US">English (US)</MenuItem>
                      <MenuItem value="es-ES">Español</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isRequired}
                        onChange={e => setFormData({ ...formData, isRequired: e.target.checked })}
                      />
                    }
                    label="Obrigatório"
                  />
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Conteúdo do Termo *
                </Typography>
                <Box
                  sx={{
                    '& .quill': {
                      height: '400px',
                      '& .ql-container': {
                        height: 'calc(100% - 42px)'
                      }
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value: string) => setFormData({ ...formData, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Digite o conteúdo do termo aqui..."
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} variant="outlined" disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Salvando...
                </>
              ) : editingDocument ? (
                'Salvar Nova Versão'
              ) : (
                'Criar Documento'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProductScopeGate>
  );
};

export default TermsPage;
